import { useState, useCallback, useEffect } from "react";
import { useAlerts } from "@/hooks";
import {
    useGetOrgsQuery,
    useGetPortfoliosQuery,
    useLazyGetAccountsByPortfolioQuery,
} from "../services/entityManagementApi";
import { parsePortfolioToRow, parseAccountToRow } from "../utils/parsers";
import { IAGPortfolioDataRow } from "@pulse-ui/types/components/AGTable";
import { getErrorMessage } from "@/utils/getErrorMessage";

/**
 * Main hook for EntityManagement page state management
 * Handles org/portfolio/account data fetching with lazy loading
 */
export const useEntityManagement = () => {
    const { showError } = useAlerts();

    // RTK Query hooks
    const { data: orgsData, isLoading: isLoadingOrgs, error: orgsError } = useGetOrgsQuery();
    const orgId = orgsData?.org_id;

    const {
        data: portfoliosData,
        isLoading: isLoadingPortfolios,
        error: portfoliosError,
    } = useGetPortfoliosQuery(orgId ?? "", {
        skip: !orgId,
    });

    const [getAccountsByPortfolio] = useLazyGetAccountsByPortfolioQuery();

    // Local state
    const [dataMap, setDataMap] = useState<Map<string, IAGPortfolioDataRow>>(new Map());
    const [openRowPortfolioIds, setOpenRowPortfolioIds] = useState<string[]>([]);

    // Build data map from portfolios
    useEffect(() => {
        if (portfoliosData) {
            // Unwrap the data structure - API returns {data: [...]} but transformResponse might not be applied due to cache
            const portfoliosArray = Array.isArray(portfoliosData)
                ? portfoliosData
                : (portfoliosData as any)?.data || [];

            setDataMap((prevMap) => {
                const newMap = new Map<string, IAGPortfolioDataRow>();
                portfoliosArray.forEach((portfolio: any) => {
                    const existingRow = prevMap.get(portfolio.portfolio_id);
                    const parsedRow = parsePortfolioToRow(portfolio);
                    newMap.set(portfolio.portfolio_id, {
                        ...parsedRow,
                        // Preserve existing accounts if already loaded
                        accounts: existingRow?.accounts ?? [],
                    });
                });
                return newMap;
            });
        }
    }, [portfoliosData]);

    // Show errors
    useEffect(() => {
        if (orgsError) {
            showError(`Failed to load organization: ${getErrorMessage(orgsError)}`);
        }
    }, [orgsError, showError]);

    useEffect(() => {
        if (portfoliosError) {
            showError(`Failed to load portfolios: ${getErrorMessage(portfoliosError)}`);
        }
    }, [portfoliosError, showError]);

    /**
     * Handles row expansion with lazy loading
     * Fetches accounts only when portfolio row is expanded
     */
    const handleToggleRowExpanded = useCallback(
        async (params: any) => {
            const portfolioId = params.value;
            let portfolio = dataMap.get(portfolioId);

            if (!portfolio) return;

            if (params.node.expanded) {
                // Collapsing
                params.node.setExpanded(false);
                setOpenRowPortfolioIds((prev) => prev.filter((id) => id !== portfolioId));
            } else {
                // Expanding - fetch accounts if not already loaded
                if (portfolio.accounts.length === 0) {
                    try {
                        const { data: accountsData } = await getAccountsByPortfolio(portfolioId);

                        if (accountsData) {
                            // Unwrap the data structure - API returns {data: [...]} but transformResponse might not be applied
                            const accountsArray = Array.isArray(accountsData)
                                ? accountsData
                                : (accountsData as any)?.data || [];

                            const parsedAccounts = accountsArray.map((account: any) =>
                                parseAccountToRow(account, portfolioId)
                            );

                            const updatedPortfolio: IAGPortfolioDataRow = {
                                ...portfolio,
                                accounts: parsedAccounts,
                            };

                            setDataMap((prev) => {
                                const updated = new Map(prev);
                                updated.set(portfolioId, updatedPortfolio);
                                return updated;
                            });
                        }
                    } catch (error) {
                        showError(`Failed to load accounts: ${getErrorMessage(error)}`);
                        return;
                    }
                }

                params.node.setExpanded(true);
                setOpenRowPortfolioIds((prev) => [...prev, portfolioId]);
            }
        },
        [dataMap, getAccountsByPortfolio, showError]
    );

    /**
     * Refreshes accounts for a specific portfolio
     * Called after account mutations to update the UI
     */
    const refetchAccountsForPortfolio = useCallback(async (portfolioId: string) => {
        const portfolio = dataMap.get(portfolioId);
        if (!portfolio) return;

        try {
            const { data: accountsData } = await getAccountsByPortfolio(portfolioId);

            if (accountsData) {
                const accountsArray = Array.isArray(accountsData)
                    ? accountsData
                    : (accountsData as any)?.data || [];

                const parsedAccounts = accountsArray.map((account: any) =>
                    parseAccountToRow(account, portfolioId)
                );

                const updatedPortfolio: IAGPortfolioDataRow = {
                    ...portfolio,
                    accounts: parsedAccounts,
                };

                setDataMap((prev) => {
                    const updated = new Map(prev);
                    updated.set(portfolioId, updatedPortfolio);
                    return updated;
                });
            }
        } catch (error) {
            showError(`Failed to refresh accounts: ${getErrorMessage(error)}`);
        }
    }, [dataMap, getAccountsByPortfolio, showError]);

    const loading = isLoadingOrgs || isLoadingPortfolios;

    return {
        orgData: orgsData,
        dataMap,
        loading,
        openRowPortfolioIds,
        handleToggleRowExpanded,
        refetchAccountsForPortfolio,
    };
};
